import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Collapse,
  useMediaQuery,
  Chip,
  Badge,
  Popper,
  Paper,
  ClickAwayListener,
  Portal,
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Palette as PaletteIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BrandIcon,
  Layers as TierIcon,
  SwapHoriz as SwapIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as CurationIcon,
  Search as SearchIcon,
  ExpandLess as CollapseIcon,
  Add as AddIcon,
  DateRange as DateRangeIcon,
  Event as EventIcon,
  Create as CreateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ReactComponent as MensureLogo } from '../../../assets/logo.svg';
import { useTheme } from '../../../design-system/providers/ThemeProvider';
import { themesList } from '../../../design-system/themes';
import { operationalElevation, operationalRadii, operationalSurfaces } from '../../../design-system/tokens';
// MIAToggle movido para UsageCounter
import { useBrand } from '../../../context/BrandContext';
import BrandSelector from '../../molecules/BrandSelector/BrandSelector';
import { useAppBarActions } from '../../../context/AppBarActionsContext';

/**
 * Header - Componente organismo para a barra superior da aplicação
 *
 * Menu do usuário estilo YouTube com todas as configurações consolidadas.
 */
const Header = ({ toggleDrawer, sidebarOpen = true }) => {
  const { user, logout } = useAuth();
  const { activeBrandData, isImpersonating, isAdminOrMensure, canCurate } = useBrand();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isDarkMode = muiTheme.palette.mode === 'dark';
  const controlSurfaces = isDarkMode ? operationalSurfaces.dark : operationalSurfaces.light;
  const controlRadius = `${operationalRadii.control}px`;
  const pillRadius = controlRadius;
  const CONTROL_SURFACE = controlSurfaces.shell;
  const CONTROL_RAISED = controlSurfaces.panel;
  const CONTROL_BORDER = muiTheme.palette.divider;
  const CONTROL_TEXT = muiTheme.palette.text.primary;
  const CONTROL_MUTED = muiTheme.palette.text.secondary;
  const CONTROL_ACCENT = isDarkMode ? '#E8EAED' : muiTheme.palette.primary.main;
  const controlSoftBg = alpha(CONTROL_TEXT, isDarkMode ? 0.035 : 0.055);
  const controlSubtleBg = alpha(CONTROL_TEXT, isDarkMode ? 0.025 : 0.04);
  const controlHoverBg = alpha(CONTROL_TEXT, isDarkMode ? 0.055 : 0.075);
  const controlActiveBg = alpha(CONTROL_TEXT, isDarkMode ? 0.09 : 0.08);
  const controlSelectedBg = alpha(CONTROL_TEXT, isDarkMode ? 0.14 : 0.10);
  const controlElevatedShadow = operationalElevation.raised;
  const [anchorEl, setAnchorEl] = useState(null);
  const [appearanceExpanded, setAppearanceExpanded] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [periodAnchorEl, setPeriodAnchorEl] = useState(null);
  const { setTheme, currentTheme } = useTheme();
  const themeId = currentTheme.id;
  const { actions } = useAppBarActions();
  const appBarActionsLeft = sidebarOpen ? 238 : 64;
  const appBarActionsRight = (canCurate || isAdminOrMensure) ? 300 : 24;
  const actionsOpen = Boolean(actionsAnchorEl && actions?.panel);
  const periodOpen = Boolean(periodAnchorEl && actions?.period);
  const periodLabels = {
    all: 'Todos',
    '24h': '24h',
    '3d': '3 dias',
    '7d': '7 dias',
    '30d': '30 dias'
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleToggleActions = (event) => {
    setActionsAnchorEl((current) => current ? null : event.currentTarget);
  };

  const handleTogglePeriod = (event) => {
    setPeriodAnchorEl((current) => current ? null : event.currentTarget);
  };

  const handlePeriodSelect = (period) => {
    actions?.period?.onChange?.(period, null, actions.period.quickDateType || 'created');
    setPeriodAnchorEl(null);
  };

  const handlePeriodTypeChange = (event, type) => {
    if (!type || !actions?.period) return;
    actions.period.onQuickDateTypeChange?.(type);
    actions.period.onChange?.(actions.period.value || 'all', null, type);
  };

  const handleCloseActions = (event) => {
    // ComposedPath preserva a árvore exata do clique, mesmo se o React desmontar o componente antes da propagação
    if (event && typeof event.composedPath === 'function') {
      const path = event.composedPath();
      const isPortal = path.some(el => 
        el && el.classList && (
          el.classList.contains('MuiDialog-root') || 
          el.classList.contains('MuiPopover-root') || 
          el.classList.contains('MuiMenu-root') || 
          el.classList.contains('MuiPickersPopper-root') ||
          el.classList.contains('MuiModal-root') ||
          el.classList.contains('MuiBackdrop-root')
        )
      );
      if (isPortal) {
        return; // Ignora o clique, foi dentro de um menu/dropdown
      }
    }
    setActionsAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setAppearanceExpanded(false);
    setSettingsExpanded(false);
  };

  const toggleSettings = () => {
    setSettingsExpanded(!settingsExpanded);
  };

  const handleBrandSettings = () => {
    navigate('/brand-settings');
    handleClose();
  };

  const handleTierSettings = () => {
    navigate('/tiers');
    handleClose();
  };

  const handleChangeBrand = () => {
    navigate('/brand-selection');
    handleClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/admin');
    handleClose();
  };

  // Ir para o painel administrativo
  const handleGoToAdminPanel = () => {
    navigate('/admin');
  };

  const toggleAppearance = () => {
    setAppearanceExpanded(!appearanceExpanded);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: CONTROL_SURFACE,
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        color: CONTROL_TEXT,
        boxShadow: 'none',
        borderBottom: 'none',
        transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease',
        '& .MuiToolbar-root': {
          minHeight: 80
        }
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={toggleDrawer}
          edge="start"
          sx={{
            mr: isMobile ? 1 : 2,
            color: CONTROL_MUTED,
            borderRadius: controlRadius,
            '&:hover': {
              color: CONTROL_TEXT,
              bgcolor: controlHoverBg
            }
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Mobile: Logo + Marca empilhados */}
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
            <MensureLogo style={{
              height: 18,
              width: 'auto',
              fill: muiTheme.palette.primary.main,
              color: muiTheme.palette.primary.main
            }} />
            <Typography variant="caption" component="div" translate="no" sx={{
              fontWeight: 600,
              fontSize: '0.65rem',
              textTransform: 'uppercase',
              letterSpacing: 'normal',
              color: 'text.secondary',
              lineHeight: 1,
              mt: 0.25
            }}>
              {activeBrandData?.name || 'MONITOR'}
            </Typography>
          </Box>
        ) : sidebarOpen ? (
          <>
            {/* Desktop: Logo à esquerda, Marca centralizada */}
            <MensureLogo style={{
              height: 24,
              width: 'auto',
              fill: CONTROL_TEXT,
              color: CONTROL_TEXT
            }} />
          </>
        ) : (
          <Box sx={{ width: 0, overflow: 'hidden' }} />
        )}

        {actions && !isMobile && (
          <Box
            sx={{
              position: 'absolute',
              left: appBarActionsLeft,
              right: appBarActionsRight,
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 0.9,
              mx: 0,
              '& .main-toolbar': {
                width: '100%',
                minWidth: 0,
                flexWrap: 'nowrap',
                gap: 1,
                alignItems: 'center'
              },
              '& .main-toolbar .MuiTypography-h5': {
                fontSize: '1.15rem'
              },
              '& .main-toolbar .MuiInputBase-root': {
                height: 38
              },
              '& .main-toolbar .MuiButton-root, & .main-toolbar .MuiIconButton-root, & .main-toolbar .MuiToggleButton-root': {
                flexShrink: 0
              }
            }}
          >
            {actions.period && !actions.panel && (
              <>
                <Button
                  size="small"
                  startIcon={<DateRangeIcon />}
                  onClick={handleTogglePeriod}
                  sx={{
                    height: 38,
                    px: 1.35,
                    borderRadius: pillRadius,
                    color: periodOpen ? CONTROL_TEXT : alpha(CONTROL_TEXT, 0.72),
                    bgcolor: periodOpen ? controlActiveBg : controlSubtleBg,
                    fontWeight: 800,
                    textTransform: 'none',
                    minWidth: 0,
                    flexShrink: 0,
                    '& .MuiButton-startIcon': { mr: 0.65 },
                    '&:hover': {
                      color: CONTROL_TEXT,
                      bgcolor: controlActiveBg
                    }
                  }}
                >
                  {periodLabels[actions.period.value] || 'PerÃ­odo'}
                </Button>

                <Menu
                  anchorEl={periodAnchorEl}
                  open={periodOpen}
                  onClose={() => setPeriodAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 170,
                      bgcolor: CONTROL_RAISED,
                      color: CONTROL_TEXT,
                      border: `1px solid ${CONTROL_BORDER}`,
                      boxShadow: operationalElevation.modal,
                      '& .MuiMenuItem-root': {
                        fontSize: '0.86rem',
                        color: CONTROL_TEXT,
                        '&:hover': { bgcolor: controlHoverBg }
                      }
                    }
                  }}
                >
                  <Box sx={{ px: 1.25, py: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mb: 0.75,
                        color: alpha(CONTROL_TEXT, 0.62),
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Data usada
                    </Typography>
                    <ToggleButtonGroup
                      value={actions.period.quickDateType || 'created'}
                      exclusive
                      onChange={handlePeriodTypeChange}
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiToggleButton-root': {
                          py: 0.45,
                          color: alpha(CONTROL_TEXT, 0.78),
                          borderColor: CONTROL_BORDER,
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          gap: 0.5,
                          '&.Mui-selected': {
                            bgcolor: controlSelectedBg,
                            color: CONTROL_TEXT
                          },
                          '&:hover': {
                            bgcolor: controlActiveBg
                          }
                        }
                      }}
                    >
                      <ToggleButton value="published">
                        <EventIcon sx={{ fontSize: 14 }} />
                        PublicaÃ§Ã£o
                      </ToggleButton>
                      <ToggleButton value="created">
                        <CreateIcon sx={{ fontSize: 14 }} />
                        Cadastro
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Divider sx={{ borderColor: CONTROL_BORDER, my: 0.5 }} />
                  {Object.entries(periodLabels).map(([value, label]) => (
                    <MenuItem
                      key={value}
                      selected={actions.period.value === value}
                      onClick={() => handlePeriodSelect(value)}
                    >
                      {label}
                    </MenuItem>
                  ))}
                  <Divider sx={{ borderColor: CONTROL_BORDER, my: 0.5 }} />
                  <MenuItem onClick={() => handlePeriodSelect('custom')}>
                    Personalizado...
                  </MenuItem>
                </Menu>
              </>
            )}

            {actions.panel && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {actions.panel}
              </Box>
            )}

            {actions.primaryAction && (
              <Button
                variant="contained"
                size="small"
                startIcon={actions.primaryAction.icon || <AddIcon />}
                onClick={actions.primaryAction.onClick}
                sx={{
                  height: 38,
                  px: 1.85,
                  borderRadius: pillRadius,
                  bgcolor: muiTheme.palette.primary.main,
                  color: muiTheme.palette.primary.contrastText,
                  fontWeight: 800,
                  letterSpacing: '0.01em',
                  textTransform: 'none',
                  boxShadow: operationalElevation.surface,
                  flexShrink: 0,
                  '& .MuiButton-startIcon': { mr: 0.65 },
                  '&:hover': {
                    bgcolor: isDarkMode ? '#ffffff' : muiTheme.palette.primary.dark,
                    boxShadow: operationalElevation.raised
                  }
                }}
              >
                {actions.primaryAction.label}
              </Button>
            )}
          </Box>
        )}

        {user ? (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 1 }}>
            {/* Seletor de Marcas para Curadoria (admin/mensure/curator) */}
            {canCurate && !isMobile && (
              <BrandSelector />
            )}

            {/* Indicador de Modo Curadoria */}
            {isImpersonating && (
              <Chip
                icon={<CurationIcon sx={{ fontSize: 16 }} />}
                label="CURADORIA"
                size="small"
                color="warning"
                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  '& .MuiChip-icon': { ml: 0.5 }
                }}
              />
            )}

            {/* Botao para Painel Admin (admin/mensure) */}
            {isAdminOrMensure && !isMobile && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AdminIcon />}
                onClick={handleGoToAdminPanel}
                sx={{
                  textTransform: 'none',
                  borderColor: CONTROL_BORDER,
                  color: CONTROL_TEXT,
                  bgcolor: controlSoftBg,
                  borderRadius: controlRadius,
                  '&:hover': {
                    borderColor: CONTROL_ACCENT,
                    bgcolor: controlHoverBg
                  }
                }}
              >
                Painel Admin
              </Button>
            )}

            {/* MIA Toggle mudado pro modal */}

            {false && actions && !isMobile && (
              <Portal>
                <Box
                  sx={{
                    position: 'fixed',
                    top: 22,
                    right: 340,
                    zIndex: (theme) => theme.zIndex.modal + 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.65,
                    p: '5px',
                    borderRadius: '999px',
                    bgcolor: controlSoftBg,
                    border: '1px solid transparent',
                    boxShadow: controlElevatedShadow,
                    backdropFilter: 'none',
                    WebkitBackdropFilter: 'none'
                  }}
                >
                  {actions.period && (
                    <>
                      <Button
                        size="small"
                        startIcon={<DateRangeIcon />}
                        onClick={handleTogglePeriod}
                        sx={{
                          height: 38,
                          px: 1.35,
                          borderRadius: '999px',
                          color: periodOpen ? CONTROL_TEXT : alpha(CONTROL_TEXT, 0.72),
                          bgcolor: periodOpen ? controlActiveBg : controlSubtleBg,
                          boxShadow: periodOpen ? (isDarkMode ? '0 10px 24px rgba(0,0,0,0.22)' : '0 8px 20px rgba(15,23,42,0.10)') : 'none',
                          fontWeight: 800,
                          textTransform: 'none',
                          minWidth: 0,
                          '& .MuiButton-startIcon': {
                            mr: 0.65
                          },
                          '&:hover': {
                            color: CONTROL_TEXT,
                            bgcolor: controlActiveBg
                          }
                        }}
                      >
                        {periodLabels[actions.period.value] || 'Período'}
                      </Button>

                      <Menu
                        anchorEl={periodAnchorEl}
                        open={periodOpen}
                        onClose={() => setPeriodAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                          sx: {
                            mt: 1,
                            minWidth: 170,
                            bgcolor: CONTROL_RAISED,
                            color: CONTROL_TEXT,
                            border: `1px solid ${CONTROL_BORDER}`,
                            boxShadow: '0 20px 50px rgba(0,0,0,0.38)',
                            '& .MuiMenuItem-root': {
                              fontSize: '0.86rem',
                              color: CONTROL_TEXT,
                              '&:hover': { bgcolor: controlHoverBg }
                            }
                          }
                        }}
                      >
                        <Box sx={{ px: 1.25, py: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mb: 0.75,
                              color: alpha(CONTROL_TEXT, 0.62),
                              fontWeight: 800,
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase'
                            }}
                          >
                            Data usada
                          </Typography>
                          <ToggleButtonGroup
                            value={actions.period.quickDateType || 'created'}
                            exclusive
                            onChange={handlePeriodTypeChange}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiToggleButton-root': {
                                py: 0.45,
                                color: alpha(CONTROL_TEXT, 0.78),
                                borderColor: CONTROL_BORDER,
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                gap: 0.5,
                                '&.Mui-selected': {
                                  bgcolor: controlSelectedBg,
                                  color: CONTROL_TEXT
                                },
                                '&:hover': {
                                  bgcolor: controlActiveBg
                                }
                              }
                            }}
                          >
                            <ToggleButton value="published">
                              <EventIcon sx={{ fontSize: 14 }} />
                              Publicação
                            </ToggleButton>
                            <ToggleButton value="created">
                              <CreateIcon sx={{ fontSize: 14 }} />
                              Cadastro
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        <Divider sx={{ borderColor: CONTROL_BORDER, my: 0.5 }} />
                        {Object.entries(periodLabels).map(([value, label]) => (
                          <MenuItem
                            key={value}
                            selected={actions.period.value === value}
                            onClick={() => handlePeriodSelect(value)}
                          >
                            {label}
                          </MenuItem>
                        ))}
                        <Divider sx={{ borderColor: CONTROL_BORDER, my: 0.5 }} />
                        <MenuItem onClick={() => handlePeriodSelect('custom')}>
                          Personalizado...
                        </MenuItem>
                      </Menu>
                    </>
                  )}

                  {actions.panel && (
                    <IconButton
                      size="small"
                      onClick={handleToggleActions}
                      sx={{
                        width: 38,
                        height: 38,
                        color: actionsOpen ? CONTROL_TEXT : alpha(CONTROL_TEXT, 0.66),
                        bgcolor: actionsOpen ? controlActiveBg : controlSubtleBg,
                        boxShadow: actionsOpen ? (isDarkMode ? '0 10px 24px rgba(0,0,0,0.22)' : '0 8px 20px rgba(15,23,42,0.10)') : 'none',
                        borderRadius: '50%',
                        '&:hover': {
                          color: CONTROL_TEXT,
                          bgcolor: controlActiveBg
                        }
                      }}
                    >
                      <Badge
                        badgeContent={actions.activeFiltersCount || 0}
                        color="error"
                        max={9}
                        sx={{
                          '& .MuiBadge-badge': {
                            height: 16,
                            minWidth: 16,
                            fontSize: '0.65rem'
                          }
                        }}
                      >
                        {actionsOpen ? <CollapseIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
                      </Badge>
                    </IconButton>
                  )}

                  {actions.primaryAction && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={actions.primaryAction.icon || <AddIcon />}
                      onClick={actions.primaryAction.onClick}
                      sx={{
                        height: 38,
                        px: 1.85,
                        borderRadius: '999px',
                        bgcolor: muiTheme.palette.primary.main,
                        color: muiTheme.palette.primary.contrastText,
                        fontWeight: 800,
                        letterSpacing: '0.01em',
                        textTransform: 'none',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.24)',
                        '& .MuiButton-startIcon': {
                          mr: 0.65
                        },
                        '&:hover': {
                          bgcolor: isDarkMode ? '#ffffff' : muiTheme.palette.primary.dark,
                          boxShadow: '0 14px 34px rgba(0,0,0,0.30)'
                        }
                      }}
                    >
                      {actions.primaryAction.label}
                    </Button>
                  )}
                </Box>

                <Popper
                  open={actionsOpen}
                  anchorEl={actionsAnchorEl}
                  placement="bottom-end"
                  modifiers={[
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -48],
                      },
                    },
                  ]}
                  sx={{ zIndex: (theme) => theme.zIndex.modal + 11 }}
                >
                  <ClickAwayListener onClickAway={handleCloseActions} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                    <Paper
                      elevation={0}
                      sx={{
                        width: 'min(1280px, calc(100vw - 300px))',
                        minWidth: 760,
                        p: 1.5,
                        borderRadius: '22px',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(32, 33, 36, 0.92)' : 'rgba(255, 255, 255, 0.92)',
                        color: (theme) => theme.palette.text.primary,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        boxShadow: (theme) => theme.palette.mode === 'dark' 
                          ? '0 26px 70px rgba(0,0,0,0.46)' 
                          : '0 26px 70px rgba(0,0,0,0.12)',
                        backdropFilter: 'none',
                        WebkitBackdropFilter: 'none'
                      }}
                    >
                      {actions.panel}
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </Portal>
            )}

            {/* Avatar do usuário */}
            {false && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 38,
                  px: 1.6,
                  borderRadius: '999px',
                  bgcolor: alpha(CONTROL_SURFACE, 0.72),
                  border: `1px solid ${CONTROL_BORDER}`,
                  boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
                  backdropFilter: 'none',
                  WebkitBackdropFilter: 'none',
                  maxWidth: 180
                }}
              >
                <Typography
                  variant="body2"
                  component="div"
                  translate="no"
                  noWrap
                  sx={{
                    color: CONTROL_TEXT,
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    lineHeight: 1
                  }}
                >
                  {activeBrandData?.name || 'MONITOR'}
                </Typography>
              </Box>
            )}

            {isMobile && (
              false && <Box
                component="button"
                type="button"
                aria-label="abrir configurações do perfil"
                onClick={handleMenu}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.1,
                  height: 50,
                  px: 1.1,
                  pr: 1.35,
                  borderRadius: '999px',
                  bgcolor: controlSoftBg,
                  border: '1px solid transparent',
                  color: CONTROL_TEXT,
                  cursor: 'pointer',
                  boxShadow: controlElevatedShadow,
                  backdropFilter: 'none',
                  WebkitBackdropFilter: 'none',
                  maxWidth: 260,
                  font: 'inherit',
                  '&:hover': {
                    bgcolor: controlHoverBg,
                    borderColor: 'transparent'
                  }
                }}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: CONTROL_RAISED,
                    color: CONTROL_TEXT,
                    border: `1px solid ${CONTROL_BORDER}`,
                    fontSize: '0.9rem',
                    fontWeight: 800,
                    flexShrink: 0
                  }}
                >
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                  <Typography
                    variant="caption"
                    component="div"
                    translate="no"
                    noWrap
                    sx={{
                      color: alpha(CONTROL_TEXT, 0.72),
                      fontSize: '0.68rem',
                      lineHeight: 1.05
                    }}
                  >
                    {user.name || 'Usuário'}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="div"
                    translate="no"
                    noWrap
                    sx={{
                      color: CONTROL_TEXT,
                      fontSize: '0.9rem',
                      fontWeight: 800,
                      lineHeight: 1.15
                    }}
                  >
                    {`Perfil de ${activeBrandData?.name || 'Mensure'}`}
                  </Typography>
                </Box>
                <ExpandMoreIcon sx={{ fontSize: 18, color: alpha(CONTROL_TEXT, 0.78), flexShrink: 0 }} />
              </Box>
            )}
            {isMobile && (
            <IconButton
              size={isMobile ? "medium" : "large"}
              aria-label="conta do usuário"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
              sx={{
                p: isMobile ? 0.5 : 1,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: controlHoverBg
                }
              }}
            >
              <Avatar sx={{
                width: isMobile ? 28 : 32,
                height: isMobile ? 28 : 32,
                bgcolor: CONTROL_RAISED,
                color: CONTROL_TEXT,
                border: `1px solid ${CONTROL_BORDER}`
              }}>
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </Avatar>
            </IconButton>
            )}

            {/* Menu do Usuário - Estilo YouTube */}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  minWidth: 280,
                  maxWidth: 320,
                  mt: 1,
                  bgcolor: CONTROL_RAISED,
                  color: CONTROL_TEXT,
                  border: `1px solid ${CONTROL_BORDER}`,
                  boxShadow: '0 24px 64px rgba(3, 7, 12, 0.48)',
                  '& .MuiMenuItem-root': {
                    color: CONTROL_TEXT,
                    '&:hover': {
                      bgcolor: controlHoverBg
                    }
                  },
                  '& .MuiListItemIcon-root': {
                    color: CONTROL_MUTED
                  },
                  '& .MuiDivider-root': {
                    borderColor: CONTROL_BORDER
                  }
                }
              }}
            >
              {/* Seção do Usuário */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: CONTROL_SURFACE, color: CONTROL_TEXT, border: `1px solid ${CONTROL_BORDER}` }}>
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {user.name || 'Usuário'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {user.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              {/* Navegação */}
              <MenuItem onClick={handleProfile}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Meu Perfil</ListItemText>
              </MenuItem>

              <Divider />

              {/* Aparência - Expansível */}
              <MenuItem onClick={toggleAppearance}>
                <ListItemIcon>
                  <PaletteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Aparência</ListItemText>
                {appearanceExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </MenuItem>

              <Collapse in={appearanceExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Tema
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {themesList.map(theme => (
                      <Box
                        key={theme.id}
                        onClick={() => setTheme(theme.id)}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          bgcolor: themeId === theme.id ? 'primary.main' : 'background.paper',
                          color: themeId === theme.id ? 'primary.contrastText' : 'text.primary',
                          border: '1px solid',
                          borderColor: themeId === theme.id ? 'primary.main' : 'divider',
                          '&:hover': {
                            bgcolor: themeId === theme.id ? 'primary.dark' : 'action.hover',
                          }
                        }}
                      >
                        {theme.name}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Collapse>

              {/* Configurações da Marca - Expansível */}
              <MenuItem onClick={toggleSettings}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Configurações</ListItemText>
                {settingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </MenuItem>

              <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ bgcolor: 'action.hover' }}>
                  <MenuItem onClick={handleBrandSettings} sx={{ pl: 4 }}>
                    <ListItemIcon>
                      <BrandIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Config. da Marca</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={handleTierSettings} sx={{ pl: 4 }}>
                    <ListItemIcon>
                      <TierIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Regras de Tier</ListItemText>
                  </MenuItem>
                </Box>
              </Collapse>

              {/* Admin Dashboard (admin/mensure) */}
              {isAdminOrMensure && (
                <MenuItem onClick={handleSettings}>
                  <ListItemIcon>
                    <AdminIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Painel Admin</ListItemText>
                </MenuItem>
              )}

              <Divider />

              {/* Ajuda */}
              <MenuItem onClick={() => { window.open('https://mensure.com.br/ajuda', '_blank'); handleClose(); }}>
                <ListItemIcon>
                  <HelpIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Ajuda</ListItemText>
              </MenuItem>

              <Divider />

              {/* Mudar de Marca */}
              <MenuItem onClick={handleChangeBrand}>
                <ListItemIcon>
                  <SwapIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mudar de Marca</ListItemText>
              </MenuItem>

              {/* Sair */}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Sair</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <Button color="inherit" onClick={() => navigate('/login')}>
            Login
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;

